import { useState, useMemo, useEffect } from 'react';
import { BigNumber, BigNumberish } from 'ethers';
import { parseEther, formatEther } from 'ethers/lib/utils';
import { Signer, Provider } from 'zksync-ethers';

import { Position } from '../utils/data';
import { getDyFeesPerc, getPriceImpact } from '../utils/curve';
import { Side } from '../utils/types';
import { calcMinAmount } from '../utils/helpers';
import { Market } from '../contexts/MarketsContext';
import { Network } from '../data/chain';
import { getTraderProposedAmount } from '../utils/clearingHouse';

interface UseExitPositionInfoParams {
  currentPosition?: Position;
  slippage: BigNumberish;
  signerOrProvider?: Signer | Provider;
  market?: Market;
  chainConfig: Network;
  user?: string;
}

export const useExitPositionInfo = ({
  currentPosition,
  slippage,
  signerOrProvider,
  market,
  chainConfig,
  user,
}: UseExitPositionInfoParams) => {
  const [proposedAmount, setProposedAmount] = useState<BigNumber>();
  const [priceImpact, setPriceImpact] = useState<BigNumber>();
  const [dy, setDy] = useState<BigNumber>();
  const [priceImpactString, setPriceImpactString] = useState<string>();
  const [tradingFeePerc, setTradingFeePerc] = useState<BigNumber>();

  const isLong = useMemo(() => currentPosition?.direction === Side.Long, [currentPosition]);

  const exitPrice = useMemo<BigNumber | undefined>(() => {
    if (market?.price && priceImpact) {
      const currentPrice = BigNumber.from(market.price);
      return currentPrice.add(currentPrice.mulWei(priceImpact));
    }
    return undefined;
  }, [market, priceImpact]);

  const minAmount = useMemo<BigNumber>(() => {
    if (dy) {
      // Trade direction reverse
      return calcMinAmount(dy, parseEther(slippage.toString())).abs();
    }
    return BigNumber.from(0);
  }, [slippage, dy]);

  const tradingFee = useMemo<BigNumber>(
    () =>
      tradingFeePerc !== undefined && currentPosition
        ? tradingFeePerc.mulWei(currentPosition.openNotional).abs().mul('-1')
        : BigNumber.from(0),
    [tradingFeePerc, currentPosition],
  );

  const pnl = useMemo<BigNumber | undefined>(
    () =>
      currentPosition && dy && tradingFee && exitPrice
        ? BigNumber.from(currentPosition.positionSize)
            .mulWei(exitPrice)
            .add(currentPosition.openNotional)
            .sub(tradingFee)
        : undefined,
    [currentPosition, dy, tradingFee, exitPrice],
  );

  useEffect(() => {
    if (
      currentPosition?.market.id !== undefined &&
      user &&
      signerOrProvider !== undefined &&
      !BigNumber.from(currentPosition.openNotional).eq(0)
    ) {
      getTraderProposedAmount({
        chainConfig,
        marketId: currentPosition.market.id,
        user,
        signerOrProvider,
      })
        .then((res) => {
          setProposedAmount(res);
        })
        .catch(console.error);
    }
  }, [currentPosition, user, signerOrProvider, chainConfig]);

  useEffect(() => {
    if (market && proposedAmount !== undefined && signerOrProvider) {
      getPriceImpact({
        cryptoSwapAddress: market.cryptoSwapPool,
        signerOrProvider,
        isLong: !isLong, // Opposite of isLong if closing
        proposedAmount,
        currentPrice: market.price,
      })
        .then(({ priceImpact: priceImpactResult, dy: dyResult }) => {
          setPriceImpact(priceImpactResult);
          setDy(dyResult);
          const percentImpact = priceImpactResult.mul('100');
          if (percentImpact.lt(parseEther('0.01'))) {
            setPriceImpactString('< 0.01%');
          } else {
            setPriceImpactString(`${Number(formatEther(percentImpact)).toFixed(2)}%`);
          }
        })
        .catch((err) => {
          console.warn(err);
          setPriceImpactString('Error');
        });
    } else {
      setPriceImpact(undefined);
      setPriceImpactString('-');
    }
  }, [market, currentPosition, signerOrProvider, isLong, proposedAmount]);

  useEffect(() => {
    let subscribed = true;
    if (market && signerOrProvider && proposedAmount && chainConfig) {
      getDyFeesPerc({
        chainConfig,
        cryptoSwapAddress: market.cryptoSwapPool,
        signerOrProvider,
        isLong: !isLong, // reverse since closing position
        proposedAmount,
      }).then((swapFee) => {
        if (subscribed) {
          setTradingFeePerc(swapFee);
        }
      });
    }
    return () => {
      subscribed = false;
    };
  }, [isLong, market, signerOrProvider, proposedAmount, chainConfig]);

  return {
    priceImpact,
    priceImpactString,
    exitPrice,
    minAmount,
    proposedAmount,
    tradingFee,
    pnl,
  };
};
